// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import { UploadError } from "@insite/client-framework/Store/Components/OrderUpload/Handlers/BatchLoadProducts";
import addToWishList, {
    AddToWishListParameter,
} from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import loadWishListIfNeeded from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/LoadWishListIfNeeded";
import translate from "@insite/client-framework/Translate";
import { ProductDto, WishListModel } from "@insite/client-framework/Types/ApiModels";
import OrderUpload, { OrderUploadStyles } from "@insite/content-library/Components/OrderUpload";
import OrderUploadErrorsModal, {
    OrderUploadErrorsModalStyles,
} from "@insite/content-library/Components/OrderUploadErrorsModal";
import ProductSelector, { ProductSelectorStyles } from "@insite/content-library/Components/ProductSelector";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Button, { ButtonPresentationProps } from "@sc/MobiusOverrides/Button/Button";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

interface AddNewItemsStyles {
    actionButton?: ButtonPresentationProps;
    container?: InjectableCss;
    uploadInstructionsGridItem?: GridItemProps;
    orderUploadGridItem?: GridItemProps;
    productSelectorModal?: ModalPresentationProps;
    productSelectorStyles?: ProductSelectorStyles;
    actionsWrapper?: InjectableCss;
    uploadItemsButton?: ButtonPresentationProps;
    uploadItemsModal?: ModalPresentationProps;
    orderUploadStyles?: OrderUploadStyles;
    orderUploadErrorsModalStyles?: OrderUploadErrorsModalStyles;
}
export const addNewItemsStyles: AddNewItemsStyles = {};
const mapDispatchToProps = {
    loadWishListIfNeeded,
    addToWishList: makeHandlerChainAwaitable<AddToWishListParameter, WishListModel>(addToWishList),
};
interface OwnProps {
    parentId: string;
    wishList: WishListModel;
}
type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & HasShellContext;
const styles = addNewItemsStyles;
const AddNewItems = ({ parentId, wishList, addToWishList, loadWishListIfNeeded, shellContext }: Props) => {
    const toasterContext = useContext(ToasterContext);
    const [uploadItemsModalIsOpen, setUploadItemsModalIsOpen] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const canEditWishList = (wishList.allowEdit || !wishList.isSharedList) && !wishList.isAutogenerated;

    if (!canEditWishList) {
        return null;
    }

    const selectProductHandler = async (productInfo: ProductInfo) => {
        await addToWishList({ productInfos: [productInfo], selectedWishList: wishList });
        toasterContext.addToast({ body: translate("Item Added"), messageType: "success" });
    };
    const uploadItemsClickHandler = () => {
        setUploadItemsModalIsOpen(true);
    };
    const uploadItemsModalCloseHandler = () => {
        setUploadItemsModalIsOpen(false);
    };
    const uploadProductsHandler = async (products: ProductDto[]) => {
        const productInfos = products.map(o => ({
            productId: o.id,
            qtyOrdered: o.qtyOrdered,
            unitOfMeasure: o.selectedUnitOfMeasure ?? "",
        }));
        await addToWishList({ productInfos, selectedWishList: wishList });
        setUploadItemsModalIsOpen(false);
        loadWishListIfNeeded({ wishListId: wishList.id });
    };
    return (
        <StyledWrapper {...styles.container}>
            <Button
                {...styles.actionButton}
                onClick={() => setExpanded(true)}
                data-test-selector="myListDetails_AddItemsToggle"
            >
                {translate("Add New Items")}
            </Button>
            {shellContext.isEditing && ( // To Be Authored
                <GridItem {...styles.uploadInstructionsGridItem}>
                    <Zone zoneName="UploadInstructions" contentId={parentId} />
                </GridItem>
            )}
            <Modal
                isOpen={expanded}
                headline={translate("Add New Items")}
                isCloseable
                handleClose={() => setExpanded(false)}
                {...styles.productSelectorModal}
            >
                <ProductSelector
                    selectButtonTitle={translate("Add to List")}
                    skipInventoryValidation={true}
                    denyFixedConfiguration={true}
                    onSelectProduct={selectProductHandler}
                    productIsConfigurableMessage={siteMessage("ListUpload_CannotOrderConfigurable")}
                    productIsUnavailableMessage={siteMessage("Product_NotFound")}
                    extendedStyles={styles.productSelectorStyles}
                />
                <StyledWrapper {...styles.actionsWrapper}>
                    <Button
                        {...styles.uploadItemsButton}
                        onClick={uploadItemsClickHandler}
                        data-test-selector="myListDetails_uploadItems"
                    >
                        {translate("Upload Items")}
                    </Button>
                    <Button
                        {...styles.actionButton}
                        onClick={() => setExpanded(false)}
                        data-test-selector="myListDetails_DismissItemsToggle"
                    >
                        {translate("Cancel")}
                    </Button>
                </StyledWrapper>
            </Modal>
            <Modal
                {...styles.uploadItemsModal}
                headline={translate("Upload Items To List")}
                isOpen={uploadItemsModalIsOpen}
                handleClose={uploadItemsModalCloseHandler}
                data-test-selector="myListDetails_uploadItemsToListModal"
            >
                <GridContainer>
                    <GridItem {...styles.orderUploadGridItem}>
                        <OrderUpload
                            descriptionText={siteMessage("Lists_UploadLinkTooltip")}
                            checkInventory={false}
                            templateUrl="/Excel/ListUploadTemplate.xlsx"
                            onUploadProducts={uploadProductsHandler}
                            extendedStyles={styles.orderUploadStyles}
                        />
                        <OrderUploadErrorsModal
                            descriptionText={siteMessage("ListUpload_UploadError")}
                            uploadErrorText={siteMessage("ListUpload_UploadError")}
                            rowsLimitExceededText={siteMessage("ListUpload_RowsLimitExceeded")}
                            errorReasons={{
                                [UploadError.ConfigurableProduct]: siteMessage("ListUpload_CannotOrderConfigurable"),
                                [UploadError.StyledProduct]: siteMessage("ListUpload_CannotOrderStyled"),
                                [UploadError.InvalidUnit]: translate("Invalid U/M"),
                                [UploadError.NotFound]: siteMessage("Product_NotFound"),
                            }}
                            extendedStyles={styles.orderUploadErrorsModalStyles}
                        />
                    </GridItem>
                    <GridItem {...styles.uploadInstructionsGridItem}>
                        <Zone zoneName="UploadInstructions" contentId={parentId} />
                    </GridItem>
                </GridContainer>
            </Modal>
        </StyledWrapper>
    );
};
export default connect(null, mapDispatchToProps)(withIsInShell(AddNewItems));
